import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const FinalCTA = () => {
    const sectionRef = useRef(null);
    const contentRef = useRef(null);

    useEffect(() => {
        const ctx = gsap.context(() => {
            gsap.fromTo(
                contentRef.current,
                {
                    opacity: 0,
                    y: 50,
                    scale: 0.95,
                },
                {
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    duration: 1,
                    ease: 'power3.out',
                    scrollTrigger: {
                        trigger: sectionRef.current,
                        start: 'top 80%',
                        end: 'top 40%',
                        scrub: 1,
                    },
                }
            );
        }, sectionRef);

        return () => ctx.revert();
    }, []);

    return (
        <section
            ref={sectionRef}
            className="py-20 bg-gradient-to-br from-primary-red via-primary-dark to-black relative overflow-hidden"
        >
            {/* Animated Background */}
            <div className="absolute inset-0">
                <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-20"></div>
            </div>

            {/* Floating Elements */}
            <div className="absolute top-10 left-10 w-20 h-20 bg-white/10 rounded-full floating-element"></div>
            <div className="absolute bottom-10 right-10 w-32 h-32 bg-white/5 rounded-full floating-element" style={{ animationDelay: '2s' }}></div>
            <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-white/10 rounded-full floating-element" style={{ animationDelay: '4s' }}></div>

            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <div ref={contentRef} className="text-center">
                    {/* Icon */}
                    <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-8">
                        <i className="fas fa-graduation-cap text-white text-3xl"></i>
                    </div>

                    {/* Heading */}
                    <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-6 leading-tight">
                        Ready to Start Your
                        <span className="block text-yellow-300">Study Abroad Journey?</span>
                    </h2>

                    {/* Subheading */}
                    <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-2xl mx-auto">
                        Book your free consultation today and take the first step towards your global education dream.
                    </p>

                    {/* Urgency Text */}
                    <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-6 py-3 rounded-full mb-10">
                        <i className="fas fa-clock text-yellow-300"></i>
                        <span className="text-white font-semibold">Limited FREE consultation slots available!</span>
                    </div>

                    {/* CTA Buttons */}
                    <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                        {/* Primary CTA - WhatsApp */}
                        <a
                            href="https://wa.me/919000329999?text=Hi%2C%20I%20would%20like%20to%20book%20a%20free%20consultation%20for%20study%20abroad%20services."
                            target="_blank"
                            rel="noopener noreferrer"
                            className="cta-glow-button group relative inline-flex items-center gap-3 bg-white text-primary-red px-10 py-5 rounded-full font-bold text-lg transition-all duration-300 hover:scale-105 hover:shadow-2xl"
                        >
                            <i className="fab fa-whatsapp text-2xl group-hover:scale-110 transition-transform"></i>
                            <span>Book Free Consultation</span>
                            <div className="absolute inset-0 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 opacity-0 group-hover:opacity-20 transition-opacity"></div>
                        </a>

                        {/* Secondary CTA - Call */}
                        <a
                            href="tel:+919000329999"
                            className="inline-flex items-center gap-3 bg-transparent text-white border-2 border-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 hover:bg-white hover:text-primary-red hover:scale-105"
                        >
                            <i className="fas fa-phone text-xl"></i>
                            <span>Call Now (+91-9000329999)</span>
                        </a>
                    </div>

                    {/* Trust Indicators */}
                    <div className="mt-12 flex flex-wrap justify-center gap-8 text-white/80">
                        <div className="flex items-center gap-2">
                            <i className="fas fa-check-circle text-green-400"></i>
                            <span>Free Consultation</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <i className="fas fa-check-circle text-green-400"></i>
                            <span>95% Visa Success Rate</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <i className="fas fa-check-circle text-green-400"></i>
                            <span>Expert Guidance</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom Wave */}
            <div className="absolute bottom-0 left-0 right-0">
                <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z"
                        fill="white"
                    />
                </svg>
            </div>
        </section>
    );
};

export default FinalCTA;
